import './component-cart-notification.scss';
import trapFocus from '@/entrypoints/helpers/trapFocus';
import emitEvent from '@/entrypoints/helpers/cartChangeEvent';

export default class CartNotification extends HTMLElement {
    constructor() {
        super();

        this.openTrigger = document.querySelector('.js-open-mini-cart');
        this.closeTrigger = document.querySelectorAll('.js-close-mini-cart');
        this.notification = document.getElementById('cart-notification');
        this.onBodyClick = this.handleBodyClick.bind(this);
        this.loginOverlay = document.querySelector('login-overlay');
        this.errorMessage = document.querySelector('.js-mini-cart-error');
        this.errorText = document.querySelector('.js-mini-cart-error-text');
        this.closeError = document.querySelector('.js-mini-cart-error-close');
        this.productWrap = document.querySelector('.js-cart-notification-product-wrap');
        this.hasSubscriptions = false;

        if (this.openTrigger) {
            this.openTrigger.addEventListener('click', (e) => {
                this.setActiveElement(document.activeElement);
                this.open();
                e.preventDefault();
            });
        }

        if (this.closeError) {
            this.closeError.addEventListener('click', (e) => {
                this.setActiveElement(document.activeElement);
                this.closeErrorMessage();
                e.preventDefault();
            });
        }

        if (this.notification) {
            this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        }
    }

    /**
     * initEventListeners ()
     *
     * Init event listeners when HTTP request and DOM parser have returned dom nodes
    */
    initEventListeners() {
        // Cart Remove
        this.querySelectorAll('.js-close-mini-cart').forEach((closeButton) => {
            closeButton.addEventListener('click', (e) => {
                this.setActiveElement(document.activeElement);
                this.close();
                this.closeErrorMessage();
                e.preventDefault();
            });
        });

        // Cart Remove
        this.querySelectorAll('.js-mini-cart-remove').forEach((removeButton) => {
            removeButton.addEventListener('click', (e) => {
                const key = removeButton.dataset.key;

                this.changeCart(key, 0);
                this.closeErrorMessage();
                e.preventDefault();
            });
        });

        // Cart Quantity Decrease
        this.querySelectorAll('.js-mini-cart-quantity-decrease').forEach((removeButton) => {
            removeButton.addEventListener('click', (e) => {
                const key = removeButton.dataset.key;
                let quantity = removeButton.dataset.quantity;

                quantity--;

                this.changeCart(key, quantity);
                this.closeErrorMessage();
                e.preventDefault();
            });
        });

        // Cart Quantity Increase
        this.querySelectorAll('.js-mini-cart-quantity-increase').forEach((addButton) => {
            addButton.addEventListener('click', (e) => {
                const key = addButton.dataset.key;
                let quantity = addButton.dataset.quantity;

                quantity++;

                this.changeCart(key, quantity);
                this.closeErrorMessage();
                e.preventDefault();
            });
        });
    }

    /**
     * changeCart ()
     *
     * Hits the /cart/change.js endpoint
    */
    changeCart(key, quantity) {
        this.productWrap.classList.add('opacity-50', 'pointer-events-none');

        fetch('/cart/change.js', {
            method: "POST",
            headers: { 'Content-Type': 'application/json', 'Accept': `application/json` },
            body: JSON.stringify({
                'id': key,
                'quantity': quantity,
                sections: this.getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname
            })
        })
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    this.handleErrorMessage(response.description);
                    return;
                }

                emitEvent.emitEvent(response);
                this.renderContents(response);
            })
            .catch((e) => {
                /* eslint-disable */
                console.error(e);
                /* eslint-enable */
            })
            .finally(() => {
                this.productWrap.classList.remove('opacity-50', 'pointer-events-none');
            });
    }

    /**
     * updateCart ()
     *
     * Hits the /cart/update.js endpoint
    */
    updateCart(items) {
        this.productWrap.classList.add('opacity-50', 'pointer-events-none');

        fetch('/cart/update.js', {
            method: "POST",
            headers: { 'Content-Type': 'application/json', 'Accept': `application/json` },
            body: JSON.stringify(items)
        })
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    this.handleErrorMessage(response.description);
                    return;
                }

                emitEvent.emitEvent(response);
                this.renderContents(response);
            })
            .catch((e) => {
                /* eslint-disable */
                console.error(e);
                /* eslint-enable */
            })
            .finally(() => {
                this.productWrap.classList.remove('opacity-50', 'pointer-events-none');
            });
    }

    /**
     * open ()
     *
     * Open notification module
    */
    open() {
        this.classList.add('is-active');
        this.notification.classList.add('is-active');

        this.notification.addEventListener('transitionend', () => {
            this.notification.focus();
            trapFocus.init(this.notification);
        }, { once: true });

        this.initEventListeners();

        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('is-hidden');
        document.body.classList.add('overflow-hidden');
    }

    /**
     * close ()
     *
     * Close notification module
    */
    close() {
        this.classList.remove('is-active');
        this.notification.classList.remove('is-active');

        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('is-hidden');

        trapFocus.remove(this.activeElement);
        document.body.classList.remove('overflow-hidden');
    }

    /**
     * renderContents ()
     *
     * Render the content passed back by the DOM parser function
    */
    renderContents(parsedState) {
        this.getSectionsToRender().forEach((section => {
            document.getElementById(section.id).innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }));
        this.open();
    }

    /**
     * getSectionsToRender ()
     *
     * Array of IDs for content sections to returned by the DOM parser function
    */
    getSectionsToRender() {
        return [
            {
                id: 'cart-notification-product'
            },
            {
                id: 'cart-notification-total'
            },
            {
                id: 'cart-notification-delivery'
            },
            {
                id: 'cart-icon-bubble'
            }
        ];
    }

    /**
     * getSectionInnerHTML ()
     *
     * DOM parser function to get contents inner HTML
    */
    getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(selector).innerHTML;
    }

    /**
     * handleBodyClick ()
     *
     * Close notification if user clicks outside the notification
    */
    handleBodyClick(evt) {
        const target = evt.target;
        if (target !== this.notification && target !== this.loginOverlay && target !== this.openTrigger && !target.closest('cart-notification') && !target.closest('login-overlay')) {
            const disclosure = target.closest('details-disclosure, header-menu');
            this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
            this.close();
        }
    }

    /**
     * setActiveElement ()
     *
     * Set active element of clicked target
    */
    setActiveElement(element) {
        this.activeElement = element;
    }


    /**
     * handleErrorMessage ()
     *
     * Translates error module onto page
    */
    handleErrorMessage(error) {
        this.errorText.innerHTML = error;
        this.errorMessage.classList.remove('hidden');
        this.errorMessage.classList.add('flex');
    }

    /**
     * closeErrorMessage ()
     *
     * Event listener to close error module
    */
    closeErrorMessage() {
        this.errorMessage.classList.remove('flex');
        this.errorMessage.classList.add('hidden');
    }
}
